import { useCallback, useEffect, useState } from 'react';
import { ErroApi } from '../lib/api';
import { buscarParceiroDeDeus, reais, type ParceiroDeDeus } from '../lib/financeiro';
import { Alerta, Cartao } from './Interface';

function diaCurto(segunda: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'America/Sao_Paulo',
    day: '2-digit',
    month: 'short',
  })
    .format(new Date(`${segunda}T12:00:00-03:00`))
    .replace('.', '');
}

/**
 * O Parceiro de Deus que cada 12 lanca por semana, somado por mes: uma linha
 * por equipe, o total de cada uma e o total geral. Quem lanca e o 12 (ou o
 * ADM dele), na pagina da equipe, aba Relatorio.
 */
export default function FinanceiroParceiroDeDeus({ ano, mes }: { ano: number; mes: number }) {
  const [dados, setDados] = useState<ParceiroDeDeus | null>(null);
  const [erro, setErro] = useState<string | null>(null);

  const carregar = useCallback(async () => {
    try {
      setDados(await buscarParceiroDeDeus(ano, mes));
      setErro(null);
    } catch (e) {
      // ARMADILHA 12: erro do usuario aparece na tela, nunca some
      setErro(e instanceof ErroApi ? e.message : 'Não consegui carregar o Parceiro de Deus.');
    }
  }, [ano, mes]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  if (erro) return <Alerta>{erro}</Alerta>;
  if (!dados) return <p className="text-sm text-tinta-400">Carregando...</p>;

  return (
    <div className="space-y-4">
      <Cartao className="overflow-x-auto p-0">
        <table className="w-full min-w-[640px] text-sm">
          <thead>
            <tr className="bg-tinta-900 text-left text-xs font-bold uppercase tracking-wide text-white">
              <th className="px-4 py-2.5">Equipe</th>
              {dados.semanas.map((s) => (
                <th key={s} className="px-2 py-2.5 text-right">
                  {diaCurto(s)}
                </th>
              ))}
              <th className="px-4 py-2.5 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {dados.equipes.map((e, i) => (
              <tr key={e.id} className={`border-b border-tinta-100 ${i % 2 ? 'bg-tinta-50' : ''}`}>
                <td className="px-4 py-2 font-semibold text-tinta-900">{e.nome}</td>
                {dados.semanas.map((s) => {
                  const valor = Number(e.porSemana[s] ?? 0);
                  return (
                    <td
                      key={s}
                      className={`px-2 py-2 text-right ${valor > 0 ? 'text-tinta-900' : 'text-tinta-300'}`}
                    >
                      {reais(valor)}
                    </td>
                  );
                })}
                <td
                  className={`px-4 py-2 text-right font-bold ${
                    e.total > 0 ? 'text-tinta-900' : 'text-tinta-300'
                  }`}
                >
                  {reais(e.total)}
                </td>
              </tr>
            ))}
            {dados.equipes.length === 0 && (
              <tr>
                <td className="px-4 py-6 text-center text-tinta-400" colSpan={dados.semanas.length + 2}>
                  Nenhuma equipe ativa.
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr className="bg-tinta-900 text-white">
              <td className="px-4 py-2.5 text-xs font-bold uppercase tracking-wide">Total geral</td>
              <td colSpan={dados.semanas.length} />
              <td className="px-4 py-2.5 text-right text-base font-black">{reais(dados.totalGeral)}</td>
            </tr>
          </tfoot>
        </table>
      </Cartao>

      <p className="text-xs text-tinta-400">
        Cada 12 lança o Parceiro de Deus da equipe dele por semana, na página da equipe (aba
        Relatório). Lançar de novo na mesma semana substitui o valor.
      </p>
    </div>
  );
}
